import React, { useEffect, useMemo, useRef, useState } from "react";

import InitialAvatar from "../../../../../Components/Avatar";
import Button from "../../../../../Components/Button";
import CustomCheckbox from "../../../../../Components/CheckCircle";
import CommonInput from "../../../../../Components/Input";
import CommonModal from "../../../../../Components/Modal";
import RadioButton from "../../../../../Components/RadioButton";
import { useAppDispatch, useAppSelector } from "../../../../../Store/Hooks";
import {
  clearMonitorAccountsError,
  fetchMonitoredAccounts,
  saveMonitoredAccounts,
} from "../../../../../Store/Reducers/AlwaysOnChannelSlice";
import {
  clearCreateWatchListError,
  clearCreateWatchListSuccess,
  createWatchList,
  updateWatchList,
} from "../../../../../Store/Reducers/OpsSlice";
import { getInitials } from "../../../../../Utils/Helper/Helper";

export interface WatchlistPickerAccount {
  id: string;
  name: string;
  initials: string;
  location?: string;
  revenue?: string;
  tier?: string;
}

type SaveMode = "watchlist" | "monitor";

export interface CreateWatchlistModalSuccess {
  type: SaveMode;
  watchlistName?: string;
  accountIds: string[];
}

export interface EditWatchListTarget {
  watch_list_id: string;
  watch_list_name: string;
  customer_ids: string[];
}

interface CreateWatchlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  pickerAccounts: WatchlistPickerAccount[];
  initialSelectedIds?: string[];
  editWatchList?: EditWatchListTarget | null;
  title?: string;
  nameLabel?: string;
  namePlaceholder?: string;
  submitLabel?: string;
  onSuccess?: (result: CreateWatchlistModalSuccess) => void;
}

const CreateWatchlistModal: React.FC<CreateWatchlistModalProps> = ({
  isOpen,
  onClose,
  pickerAccounts,
  initialSelectedIds,
  editWatchList = null,
  title = "Save Accounts",
  nameLabel = "Watchlist Name",
  namePlaceholder = "Enter Watchlist Name",
  submitLabel = "Create Watchlist",
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { data: watchListsData } = useAppSelector(
    state => state.ops.watchLists
  );
  const { loading: createWatchListLoading } = useAppSelector(
    state => state.ops.createWatchList
  );
  const { loading: updateWatchListLoading } = useAppSelector(
    state => state.ops.updateWatchList
  );
  const {
    items: monitoredAccounts,
    saving: saveMonitoredAccountsLoading,
    error: monitorAccountsError,
  } = useAppSelector(state => state.alwaysOnChannel.monitorAccounts);

  const isEditMode = Boolean(editWatchList?.watch_list_id);
  const [step, setStep] = useState<1 | 2>(1);
  const [saveMode, setSaveMode] = useState<SaveMode>("watchlist");
  const [watchlistName, setWatchlistName] = useState("");
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const wasOpenRef = useRef(false);

  const allPickerAccountIds = useMemo(
    () => pickerAccounts.map(account => account.id),
    [pickerAccounts]
  );

  const defaultWatchlistAccountIds = useMemo(() => {
    const availableIds = new Set(allPickerAccountIds);
    const seedIds = isEditMode
      ? editWatchList?.customer_ids || []
      : initialSelectedIds || [];
    return seedIds.filter(id => availableIds.has(id));
  }, [
    allPickerAccountIds,
    editWatchList?.customer_ids,
    initialSelectedIds,
    isEditMode,
  ]);

  const defaultMonitorAccountIds = useMemo(
    () => (initialSelectedIds?.length ? defaultWatchlistAccountIds : []),
    [defaultWatchlistAccountIds, initialSelectedIds]
  );

  const monitoredAccountIds = useMemo(
    () =>
      Array.from(
        new Set(
          monitoredAccounts
            .map(account =>
              String(
                account.customer_id || account.account_id || account.id || ""
              ).trim()
            )
            .filter(Boolean)
        )
      ),
    [monitoredAccounts]
  );

  /** Reset form only when the modal opens — not on every parent re-render. */
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;

    wasOpenRef.current = true;
    setStep(1);
    setSaveMode("watchlist");
    setWatchlistName(isEditMode ? editWatchList?.watch_list_name || "" : "");
    setSelectedAccountIds(defaultWatchlistAccountIds);
    dispatch(clearCreateWatchListError());
    dispatch(clearCreateWatchListSuccess());
    dispatch(clearMonitorAccountsError());
  }, [
    isOpen,
    defaultWatchlistAccountIds,
    dispatch,
    editWatchList?.watch_list_name,
    isEditMode,
  ]);

  const normalizedName = watchlistName.trim().toLowerCase();
  const isDuplicateName = useMemo(
    () =>
      !!normalizedName &&
      (watchListsData || []).some(watchList => {
        if (
          isEditMode &&
          watchList.watch_list_id === editWatchList?.watch_list_id
        ) {
          return false;
        }
        return (
          watchList.watch_list_name?.trim().toLowerCase() === normalizedName
        );
      }),
    [watchListsData, normalizedName, isEditMode, editWatchList?.watch_list_id]
  );

  const handleClose = () => {
    setStep(1);
    setSaveMode("watchlist");
    setWatchlistName("");
    setSelectedAccountIds([]);
    dispatch(clearCreateWatchListError());
    dispatch(clearCreateWatchListSuccess());
    dispatch(clearMonitorAccountsError());
    onClose();
  };

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextMode = event.target.value as SaveMode;
    setSaveMode(nextMode);
    setSelectedAccountIds(
      nextMode === "watchlist"
        ? defaultWatchlistAccountIds
        : defaultMonitorAccountIds
    );
    dispatch(clearCreateWatchListError());
    dispatch(clearCreateWatchListSuccess());
    dispatch(clearMonitorAccountsError());
  };

  const handleAccountToggle = (accountId: string) => {
    setSelectedAccountIds(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAccountIds.length === pickerAccounts.length) {
      setSelectedAccountIds([]);
    } else {
      setSelectedAccountIds(pickerAccounts.map(account => account.id));
    }
  };

  const handleSave = async () => {
    if (!isEditMode && saveMode === "monitor") {
      if (selectedAccountIds.length === 0) return;

      const mergedAccountIds = Array.from(
        new Set([...monitoredAccountIds, ...selectedAccountIds])
      );

      const result = await dispatch(
        saveMonitoredAccounts({
          account_ids: mergedAccountIds,
        })
      );

      if (result.meta.requestStatus === "fulfilled") {
        await dispatch(fetchMonitoredAccounts());
        onSuccess?.({
          type: "monitor",
          accountIds: selectedAccountIds,
        });
        handleClose();
      }
      return;
    }

    if (
      !watchlistName.trim() ||
      selectedAccountIds.length === 0 ||
      isDuplicateName
    ) {
      return;
    }

    if (isEditMode && editWatchList?.watch_list_id) {
      const result = await dispatch(
        updateWatchList({
          watch_list_id: editWatchList.watch_list_id,
          watch_list_name: watchlistName.trim(),
          customer_ids: selectedAccountIds,
        })
      );

      if (result.meta.requestStatus === "fulfilled") {
        onSuccess?.({
          type: "watchlist",
          watchlistName: watchlistName.trim(),
          accountIds: selectedAccountIds,
        });
        handleClose();
      }
      return;
    }

    const result = await dispatch(
      createWatchList({
        watch_list_name: watchlistName.trim(),
        customer_ids: selectedAccountIds,
      })
    );

    if (result.meta.requestStatus === "fulfilled") {
      onSuccess?.({
        type: "watchlist",
        watchlistName: watchlistName.trim(),
        accountIds: selectedAccountIds,
      });
      handleClose();
    }
  };

  const isSaving =
    createWatchListLoading ||
    updateWatchListLoading ||
    saveMonitoredAccountsLoading;
  const alreadyMonitoredVisibleCount = useMemo(
    () =>
      pickerAccounts.filter(account => monitoredAccountIds.includes(account.id))
        .length,
    [pickerAccounts, monitoredAccountIds]
  );

  /** Keep initially selected accounts at the top of the confirm list. */
  const orderedPickerAccounts = useMemo(() => {
    const initialIds = new Set(
      saveMode === "monitor" && !isEditMode
        ? defaultMonitorAccountIds
        : defaultWatchlistAccountIds
    );
    if (initialIds.size === 0) return pickerAccounts;

    const selected: WatchlistPickerAccount[] = [];
    const rest: WatchlistPickerAccount[] = [];
    pickerAccounts.forEach(account => {
      if (initialIds.has(account.id)) {
        selected.push(account);
      } else {
        rest.push(account);
      }
    });
    return [...selected, ...rest];
  }, [
    pickerAccounts,
    defaultWatchlistAccountIds,
    defaultMonitorAccountIds,
    saveMode,
    isEditMode,
  ]);

  const modalTitle = isEditMode ? "Edit Watchlist" : title;
  const modalSubmitLabel = isEditMode ? "Save Watchlist" : submitLabel;

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={handleClose}
      title={modalTitle}
      size="2xl"
      usePortal
      containerClassName="z-[1070]"
    >
      <div className="create-group-modal-content">
        {step === 1 ? (
          <div className="mt-2">
            {!isEditMode && (
              <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="mb-3 text-sm font-semibold text-gray-900">
                  Save selected accounts as
                </p>
                <div className="space-y-3">
                  <RadioButton
                    name="saveMode"
                    value="watchlist"
                    checked={saveMode === "watchlist"}
                    onChange={handleModeChange}
                    label={
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Watchlist
                        </div>
                        <div className="text-xs text-gray-500">
                          Create a named watchlist for this set of accounts.
                        </div>
                      </div>
                    }
                  />
                  <RadioButton
                    name="saveMode"
                    value="monitor"
                    checked={saveMode === "monitor"}
                    onChange={handleModeChange}
                    label={
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Monitor
                        </div>
                        <div className="text-xs text-gray-500">
                          Monitor accounts directly without giving them a
                          watchlist name.
                        </div>
                      </div>
                    }
                  />
                </div>
              </div>
            )}
            {saveMode === "watchlist" || isEditMode ? (
              <>
                <CommonInput
                  label={nameLabel}
                  name="watchlistName"
                  type="text"
                  value={watchlistName}
                  placeholder={namePlaceholder}
                  onChange={e => setWatchlistName(e.target.value)}
                  required
                  disabled={isSaving}
                />
                <p className="text-xs text-gray-500 mt-2 mb-0">
                  {isEditMode
                    ? "Update the watchlist name, then confirm accounts."
                    : "Add a clear and unique watchlist name before confirming accounts."}
                </p>
                {isDuplicateName && (
                  <p className="text-red-600 text-sm mt-2 mb-0">
                    A watchlist with this name already exists. Please use a
                    different name.
                  </p>
                )}
              </>
            ) : (
              <p className="text-xs text-gray-500 mt-2 mb-0">
                Choose the accounts to monitor in the next step. Existing
                monitored accounts stay included.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-4">
              <p className="text-sm font-medium m-0">
                {selectedAccountIds.length} Accounts{" "}
                {saveMode === "watchlist" || isEditMode
                  ? "Selected"
                  : "Ready to Monitor"}
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-3 max-h-72 overflow-auto bg-white">
              {pickerAccounts.length > 0 && (
                <div className="flex items-center justify-between border-b pb-2 mb-3">
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      {saveMode === "watchlist" || isEditMode
                        ? "Selected accounts"
                        : "Accounts to monitor"}
                    </span>
                    <p className="text-xs text-gray-500 m-0 mt-1">
                      {saveMode === "watchlist" || isEditMode
                        ? "Confirm which accounts to include in this watchlist."
                        : "Choose which accounts should be added to monitoring."}
                    </p>
                    {!isEditMode &&
                      saveMode === "monitor" &&
                      alreadyMonitoredVisibleCount > 0 && (
                        <p className="text-xs text-gray-500 m-0 mt-1">
                          {alreadyMonitoredVisibleCount} account
                          {alreadyMonitoredVisibleCount === 1 ? "" : "s"}{" "}
                          already monitored in this list will stay included.
                        </p>
                      )}
                  </div>
                  <CustomCheckbox
                    checked={
                      selectedAccountIds.length === pickerAccounts.length &&
                      pickerAccounts.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                </div>
              )}
              {orderedPickerAccounts.length > 0 ? (
                <div className="space-y-2">
                  {orderedPickerAccounts.map(account => (
                    <div
                      key={account.id}
                      className="flex items-center rounded-md px-2 py-2 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <CustomCheckbox
                          checked={selectedAccountIds.includes(account.id)}
                          onChange={() => handleAccountToggle(account.id)}
                        />
                        <InitialAvatar
                          initials={
                            account.initials || getInitials(account.name)
                          }
                        />
                        <p className="text-sm font-semibold text-gray-900 m-0 truncate">
                          {account.name}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 m-0">
                  No accounts available.
                </p>
              )}
            </div>
            {saveMode === "monitor" && !isEditMode && monitorAccountsError && (
              <p className="text-red-600 text-sm mt-3 mb-0">
                {monitorAccountsError}
              </p>
            )}
          </>
        )}
        <div className="flex gap-2 justify-end mt-6">
          <Button
            variant="outline-primary"
            className="btn-lg"
            onClick={() => (step === 1 ? handleClose() : setStep(1))}
            disabled={isSaving}
          >
            Back
          </Button>
          {step === 1 ? (
            <Button
              variant="primary"
              onClick={() => {
                setStep(2);
              }}
              disabled={
                ((saveMode === "watchlist" || isEditMode) &&
                  (!watchlistName.trim() || isDuplicateName)) ||
                isSaving ||
                pickerAccounts.length === 0
              }
              className="btn-lg"
            >
              Next
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={
                ((saveMode === "watchlist" || isEditMode) &&
                  !watchlistName.trim()) ||
                selectedAccountIds.length === 0 ||
                ((saveMode === "watchlist" || isEditMode) && isDuplicateName) ||
                isSaving
              }
              className="btn-lg"
            >
              {isSaving
                ? "Saving..."
                : saveMode === "watchlist" || isEditMode
                  ? modalSubmitLabel
                  : "Monitor Accounts"}
            </Button>
          )}
        </div>
      </div>
    </CommonModal>
  );
};

export default CreateWatchlistModal;
